import type { Role } from './types';

/** Merkt sich die laufende Fahrt, damit ein Neuladen nichts verliert. */

const KEY = 'r2f.session';

export interface Session {
  rideId: string;
  token: string;
  role: Role;
}

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.rideId === 'string' && typeof parsed?.token === 'string') {
      return parsed as Session;
    }
  } catch {
    // Kaputter Eintrag ist kein Grund, die App nicht zu starten.
  }
  return null;
}

export function saveSession(session: Session): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    // Privater Modus o. AE. - die App läuft dann eben ohne Merkfunktion.
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* egal */
  }
}

/**
 * Mehrere Fahrten gleichzeitig als Fahrer offen zu haben, ist normal - eine
 * Standortübertragung läuft trotzdem immer nur für eine davon (siehe
 * `App.tsx`). Diese Liste hält fest, welche Codes noch nicht beendet oder
 * verworfen sind, damit man sie nach dem Wegnavigieren wiederfindet.
 */

const LIST_KEY = 'r2f.driverSessions';

export interface DriverSessionEntry extends Session {
  createdAt: number;
}

function isDriverSessionEntry(value: unknown): value is DriverSessionEntry {
  const entry = value as Partial<DriverSessionEntry> | null;
  return (
    typeof entry?.rideId === 'string' &&
    typeof entry?.token === 'string' &&
    typeof entry?.createdAt === 'number'
  );
}

function readDriverSessions(): DriverSessionEntry[] {
  try {
    const raw = localStorage.getItem(LIST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isDriverSessionEntry) : [];
  } catch {
    return [];
  }
}

function writeDriverSessions(list: DriverSessionEntry[]): DriverSessionEntry[] {
  try {
    localStorage.setItem(LIST_KEY, JSON.stringify(list));
  } catch {
    // egal - die Liste lebt dann nur fuer diese Sitzung.
  }
  return list;
}

/** Neueste zuerst. */
export function loadDriverSessions(): DriverSessionEntry[] {
  return readDriverSessions().sort((a, b) => b.createdAt - a.createdAt);
}

/** Anlegen oder erneut nach vorn holen (z. B. beim erneuten Öffnen). */
export function addDriverSession(session: Session): DriverSessionEntry[] {
  const rest = readDriverSessions().filter((entry) => entry.rideId !== session.rideId);
  return writeDriverSessions([{ ...session, createdAt: Date.now() }, ...rest]);
}

export function removeDriverSession(rideId: string): DriverSessionEntry[] {
  return writeDriverSessions(readDriverSessions().filter((entry) => entry.rideId !== rideId));
}
